import { useI18n } from '@/hooks/useI18n';
import { Link } from 'react-router-dom';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import { useEfficiency } from '@/hooks/queries/useEfficiency';
import { formatNumber, formatPeriodRange } from '@/lib/format';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

interface EfficiencySectionProps {
  periodDays: number;
}

export function EfficiencySection({ periodDays }: EfficiencySectionProps) {
  const { t } = useI18n();
  const { data, isLoading, isError, error, refetch } = useEfficiency({ periodDays, limit: 20 });

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Vues par publication', 'Views per post')}
        subtitle={t(
          `Vues gagnées ${formatPeriodRange(periodDays)}, divisées par le nombre total de publications du profil.`,
          `Views gained ${formatPeriodRange(periodDays)}, divided by the profile’s total post count.`,
        )}
      />
      <Tile padded>
        {isError ? (
          <ErrorInline error={error} onRetry={() => void refetch()} />
        ) : isLoading && !data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-[8px]" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <AnalyticsEmpty
            title={t('Aucune donnée', 'No data')}
            description={t(
              'Il faut des vues mesurées et un nombre de publications connu pour calculer ce ratio.',
              'Measured views and a known post count are needed to calculate this ratio.',
            )}
          />
        ) : (
          <div className="max-w-full overflow-x-auto rounded-[10px] border border-line-1">
            <table className="w-full min-w-[480px] text-[13px]">
              <thead className="bg-bg-2 text-left">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold text-text-2">
                    {t('Compte', 'Account')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Vues gagnées', 'Views gained')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Publications du profil', 'Profile posts')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Vues / post', 'Views / post')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.accountId}
                    className="border-t border-line-1 transition-colors hover:bg-bg-2"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/accounts/${row.accountId}`}
                        className="inline-flex min-h-10 items-center rounded font-medium text-text-1 underline-offset-4 hover:text-violet hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                      >
                        @{row.username}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-1">
                      {formatNumber(row.viewsAllTime)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-3">
                      {formatNumber(row.postCount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-1">
                      {formatNumber(Math.round(row.viewsPerPost))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tile>
    </section>
  );
}
